import { TEstimateItem } from '../types/estimate/estimate';
import { roundDown, getEstimateItemByIndexes } from '../utils/estimate';

/**
 * ====================
 * 共通
 * ====================
 */

/**
 * 数量×単価で計算するときは、数量・単価は小数可能で、合計は小数点以下切り捨て
 * @param quantity 数量
 * @param unitPrice 単価
 * @returns 数量×単価 (小数点以下切り捨て)
 */
export const calcTotalFromQuantityAndUnitPrice = (
  quantity: number,
  unitPrice: number
) => {
  let factor = 1000;
  let result = (quantity * factor * (unitPrice * factor)) / (factor * factor); // JavaScriptの浮動小数点の誤差を調整
  return Math.floor(result);
};

/**
 * ====================
 * 見積書
 * ====================
 */

/**
 * 見積金額 = 数量×単価 (小数点切り捨て)
 * @param quantity 数量
 * @param sellingUnitPrice 単価
 * @returns 見積金額
 */
export const calcAllSellingPrice = (
  quantity: number,
  sellingUnitPrice: number
): number => {
  return calcTotalFromQuantityAndUnitPrice(quantity, sellingUnitPrice);
};

/**
 * 原価金額 = 原価数量×原価単価 (小数点切り捨て)
 * @param costQuantity 原価数量
 * @param costUnitPrice 原価単価
 * @returns 原価金額
 */
export const calcAllCostPrice = (
  costQuantity: number,
  costUnitPrice: number
): number => {
  return calcTotalFromQuantityAndUnitPrice(costQuantity, costUnitPrice);
};

/**
 * 粗利 = 見積金額 - 原価金額
 * @param allSellingPrice 見積金額
 * @param allCostPrice 原価金額
 * @returns 粗利
 */
export const calcGrossProfit = (
  allSellingPrice: number,
  allCostPrice: number
): number => {
  return allSellingPrice - allCostPrice;
};

/**
 * 粗利率 = (見積金額 - 原価金額) / 見積金額 ) * 100 (小数第二位まで)
 * 見積金額が0の時は粗利率を0に設定
 * @param allSellingPrice 見積金額
 * @param allCostPrice 原価金額
 * @returns 粗利率
 */
export const calcGrossProfitMargin = (
  allSellingPrice: number,
  allCostPrice: number
): number => {
  if (allSellingPrice === 0) return 0;
  return roundDown(
    ((allSellingPrice - allCostPrice) / allSellingPrice) * 100,
    2
  );
};

/**
 * 粗利率から単価・見積金額を更新する
 * 粗利率が100%でない限り、原価単価・原価金額に変更はない
 * @param item 見積書項目
 */
export const updateSellingPricesFromGrossProfitMargin = (
  item: TEstimateItem
): void => {
  const sellingUnitPrice = isNaN(item.grossProfitMargin) //仕様上NaNの可能性があるため
    ? 0
    : calcSellingPricesFromGrossProfitMargin(
        item.allCostPrice,
        item.quantity,
        item.grossProfitMargin
      );
  const allSellingPrice = calcAllSellingPrice(item.quantity, sellingUnitPrice);
  item.allSellingPrice = allSellingPrice;
  item.sellingUnitPrice = sellingUnitPrice;
  item.grossProfit = calcGrossProfit(allSellingPrice, item.allCostPrice);
};

/**
 * 粗利率を100%に設定する
 * 元の原価金額が見積金額になり、原価単価は0になる
 * それに合わせて単価・原価単価も更新される
 * @param item 見積書項目
 */
export const setGrossProfitMarginTo100 = (item: TEstimateItem): void => {
  item.sellingUnitPrice = roundDown(item.allCostPrice / item.quantity, 2);
  item.allSellingPrice = calcAllSellingPrice(
    item.quantity,
    item.sellingUnitPrice
  );
  item.costUnitPrice = 0;
  item.allCostPrice = 0;
};

/**
 * 親階層へ数値の変更を反映させる
 * @param estimateItemList 見積書項目のリスト
 * @param parentsIndexes 更新したい項目に辿り着くためのインデックスのリスト
 */
export const updateParentsNumericalValues = (
  estimateItemList: TEstimateItem[],
  parentsIndexes: number[]
): void => {
  if (parentsIndexes.length == 0) return;

  // 更新したい項目
  let parentItem = getEstimateItemByIndexes(estimateItemList, parentsIndexes);

  updateNumericalValuesFromSubLayer(parentItem);

  // さらに上の階層も更新しなければいけないので、インデックスリストの末尾を削除して再帰呼び出し
  updateParentsNumericalValues(estimateItemList, parentsIndexes.slice(0, -1));
};

/**
 * 階層中の項目の見積金額を全て合計した値を計算する
 * @param items 階層中の項目のリスト
 * @returns 階層中の項目の見積金額を全て合計した値
 */
export const sumAllSellingPrices = (items: TEstimateItem[]): number => {
  return items.reduce((total, item) => {
    return (
      total +
      calcAllSellingPrice(Number(item.quantity), Number(item.sellingUnitPrice))
    );
  }, 0);
};

/**
 * 階層中の項目の原価金額を全て合計した値を計算する
 * @param items 階層中の項目のリスト
 * @returns 階層中の項目の原価金額を全て合計した値
 */
export const sumAllCostPrices = (items: TEstimateItem[]): number => {
  return items.reduce((total, item) => {
    return (
      total +
      calcAllCostPrice(Number(item.costQuantity), Number(item.costUnitPrice))
    );
  }, 0);
};

/**
 * 粗利率から単価を計算する
 * 単価 = 原価金額 / ((100 - 粗利率) / 100) / 数量 (小数点以下第二位まで)
 * @param allCostPrice 原価金額
 * @param quantity 数量
 * @param grossProfitMargin 粗利率
 * @returns 単価
 */
const calcSellingPricesFromGrossProfitMargin = (
  allCostPrice: number,
  quantity: number,
  grossProfitMargin: number
): number => {
  // 単価 = 原価金額 / ((100 - 粗利率) / 100) / 数量 (小数点以下第二位まで)
  const sellingUnitPrice = roundDown(
    Math.round(allCostPrice / ((100 - grossProfitMargin) / 100)) / quantity,
    2
  );
  return sellingUnitPrice;
};

/**
 * 子階層から各数値を更新する
 * @param item 見積書項目
 */
const updateNumericalValuesFromSubLayer = (item: TEstimateItem) => {
  item.sellingUnitPrice = sumAllSellingPrices(item.childItems);
  item.costUnitPrice = sumAllCostPrices(item.childItems);
  item.allSellingPrice = calcAllSellingPrice(
    item.quantity,
    item.sellingUnitPrice
  );
  item.allCostPrice = calcAllCostPrice(item.costQuantity, item.costUnitPrice);
  item.grossProfit = calcGrossProfit(item.allSellingPrice, item.allCostPrice);
  item.grossProfitMargin = calcGrossProfitMargin(
    item.allSellingPrice,
    item.allCostPrice
  );
};
